import React, { useEffect, useMemo, useState } from 'react';

import { FormProvider, useForm, Controller, FieldError } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { EMAIL_REGEX } from 'src/accounts/constants';
import { SelectStyle } from 'src/accounts/utils';
import { Toast } from 'src/common/components';
import { DualListBox } from 'src/common/components/DualListBox';
import { useQuery } from 'src/common/hooks/useQuery';

import { createLegalEntity, updateLegalEntity } from '../api';
import { LegalEntityFormAction, COMPANY_TYPE_OPTION, VERTICAL_TYPE_OPTION } from '../constants';
import { useLegalEntityFormContext } from '../context';

type OptionType = { label: string; value: string };

type FormType = {
  name: string;
  legal_name: string;
  ein: string;
  email: string;
  phone_number: string;
  website: string;
  street: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  company_type: OptionType;
  vertical_type: OptionType;
};

const Form: React.FC = () => {
  const { state, dispatch } = useLegalEntityFormContext();
  const { legalEntity } = state;
  const { id, company_type, vertical_type } = legalEntity;
  const [selectedAccounts, setSelectedAccounts] = useState<any>([]);
  const [queryAvailable, setQueryAvailable] = useState('');
  const { data: accountsData } = useQuery(
    `/account_admin?page=1&search=${queryAvailable}&ordering=name&basic_info=true`
  );
  const accounts = (accountsData as { results: Array<any> })?.results || [];

  const companyTypeOption = useMemo(
    () => COMPANY_TYPE_OPTION.find((ctype) => ctype.value === company_type?.toString()),
    [company_type]
  );
  const verticalTypeOption = useMemo(
    () => VERTICAL_TYPE_OPTION.find((vtype) => vtype.value === vertical_type?.toString()),
    [vertical_type]
  );

  const methods = useForm<FormType>({
    defaultValues: {
      ...legalEntity,
      company_type: companyTypeOption,
      vertical_type: verticalTypeOption,
    },
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = methods;

  useEffect(() => {
    const defaultValues = {} as FormType;
    defaultValues.name = legalEntity.name;
    defaultValues.legal_name = legalEntity.legal_name;
    defaultValues.ein = legalEntity.ein;
    defaultValues.email = legalEntity.email;
    defaultValues.phone_number = legalEntity.phone_number;
    defaultValues.website = legalEntity.website;
    defaultValues.street = legalEntity.street;
    defaultValues.city = legalEntity.city;
    defaultValues.state = legalEntity.state || 'PR';
    defaultValues.zip_code = legalEntity.zip_code;
    defaultValues.country = legalEntity.country || 'US';
    defaultValues.company_type = companyTypeOption || COMPANY_TYPE_OPTION[0];
    defaultValues.vertical_type = verticalTypeOption || VERTICAL_TYPE_OPTION[0];
    setSelectedAccounts(legalEntity?.accounts);
    reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalEntity]);

  const navigate = useNavigate();

  const handleNavigate = (): void => {
    navigate('/app/tcr/legal_entities');
  };

  const onSubmit = async (data: FormType): Promise<void> => {
    const payload = {
      ...data,
      company_type: data.company_type.value,
      vertical_type: data.vertical_type.value,
      accounts: selectedAccounts.map((selected: any) => selected.id as string),
    };
    if (!id) {
      try {
        await createLegalEntity(payload);
        handleNavigate();
        Toast.success('Changes saved successfully.');
      } catch (err) {
        Toast.error('There was an error saving changes.');
        reset({ ...data });
      }
    } else {
      try {
        await updateLegalEntity(id, payload);
        dispatch({
          type: LegalEntityFormAction.UPDATE_LEGAL_ENTITY,
          legalEntity: { ...legalEntity, ...payload },
        });
        handleNavigate();
        Toast.success('Changes saved successfully.');
      } catch (err) {
        Toast.error('There was an error saving changes.');
        reset({ ...data });
      }
    }
  };

  const formFields = [
    {
      label: 'Name',
      name: 'name',
      validations: {
        required: 'Name is required',
        minLength: { value: 5, message: 'Mínimo 5 caracteres' },
      },
      helperText: 'Brand/Marketing/DBA name of the business',
      type: 'text',
    },
    {
      label: 'Legal Name',
      name: 'legal_name',
      validations: {
        required: 'Legal name is required',
        minLength: { value: 5, message: 'Mínimo 5 caracteres' },
      },
      helperText: 'The legal name of the business',
      type: 'text',
    },
    {
      label: 'EIN',
      name: 'ein',
      validations: {
        required: 'EIN is required',
        pattern: { value: /^\d{9}$/, message: 'Length should be 9 and only numbers allowed' },
      },
      helperText:
        'An Employer Identification Number (EIN) is a nine-digit number that IRS assigns as the Tax ID of the business. It is known locally as Seguro Social Patronal.',
      type: 'number',
    },
    {
      label: 'Email',
      name: 'email',
      validations: {
        required: 'Email is required',
        pattern: {
          value: EMAIL_REGEX,
          message: 'Entered value does not match email format',
        },
      },
      type: 'email',
    },
    {
      label: 'Phone number',
      name: 'phone_number',
      validations: {
        required: 'Phone number is required',
        pattern: { value: /^\d*$/, message: 'Only numbers allowed' },
      },
      type: 'number',
    },
    {
      label: 'Website',
      name: 'website',
      validations: {},
      helperText:
        'The website/online presence of the business. Examples:</br><a href="https://www.brainhi.com" class="text-blue-1.0 underline" target="_blank">https://www.brainhi.com</a></br><a href="https://www.facebook.com/saybrainhi" class="text-blue-1.0 underline" target="_blank">https://www.facebook.com/saybrainhi</a>',
      type: 'url',
    },
    {
      label: 'Street',
      name: 'street',
      validations: {
        required: 'Street is required',
        minLength: { value: 5, message: 'Mínimo 5 caracteres' },
      },
      type: 'text',
    },
    {
      label: 'City',
      name: 'city',
      validations: { required: 'City is required' },
      type: 'text',
    },
    {
      label: 'State',
      name: 'state',
      validations: { required: 'State is required' },
      type: 'text',
      readOnly: true,
    },
    {
      label: 'Zip code',
      name: 'zip_code',
      validations: {
        required: 'Zip code is required',
        minLength: { value: 5, message: 'Mínimo 5 caracteres' },
        pattern: {
          value: /^(\d{5}|\d{5}-\d{4})$/,
          message: 'Enter a valid zip code. Allowed formats: XXXXX or XXXXX-XXXX',
        },
      },
      type: 'text',
    },
    {
      label: 'Country',
      name: 'country',
      validations: { required: 'Country is required' },
      type: 'text',
      readOnly: true,
    },
  ];

  return (
    <FormProvider {...methods}>
      <form className="flex flex-col my-6" onSubmit={handleSubmit(onSubmit)} data-testid="form">
        {formFields.map(({ label, name, validations, helperText, type, readOnly }) => (
          <div
            key={name}
            className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0 items-center"
          >
            <div className="flex-col">
              <div className="flex gap-1">
                {validations.required && <span className="text-red-1.0 font-semibold">(*)</span>}
                <h4 className="my-auto">{label}</h4>
              </div>
              {helperText && (
                <p className="text-xs" dangerouslySetInnerHTML={{ __html: helperText }} />
              )}
            </div>
            <div className="flex flex-col">
              <input
                className={`input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out ${
                  readOnly ? 'bg-gray-0.0' : ''
                }`}
                {...register(name as keyof FormType, validations)}
                type={type}
                data-testid={name}
                readOnly={readOnly} // readOnly is used because disabled inputs are not submitted
              />
              {errors[name as keyof FormType] && (
                <p className="error-text">
                  ERROR: {(errors[name as keyof FormType] as FieldError)?.message}
                </p>
              )}
            </div>
          </div>
        ))}
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Company type</h4>
          <Controller
            name="company_type"
            render={({ field }) => (
              <Select
                styles={SelectStyle}
                aria-label="company_type"
                placeholder=""
                options={COMPANY_TYPE_OPTION}
                {...field}
              />
            )}
            control={control}
            rules={{ required: true }}
            defaultValue={companyTypeOption}
          />
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Vertical type</h4>
          <Controller
            name="vertical_type"
            render={({ field }) => (
              <Select
                styles={SelectStyle}
                aria-label="vertical_type"
                placeholder=""
                options={VERTICAL_TYPE_OPTION}
                {...field}
              />
            )}
            control={control}
            rules={{ required: true }}
            defaultValue={verticalTypeOption}
          />
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0 items-center">
          <div className="flex-col">
            <h4 className="mb-2 font-semibold">Accounts:</h4>
            <p className="text-xs">Associate or disassociate accounts with the campaign details</p>
          </div>
          <div className="h-72 flex-col">
            <DualListBox
              className="h-full"
              options={accounts}
              onChange={(newValue) => setSelectedAccounts(newValue)}
              selected={selectedAccounts}
              onSearch={setQueryAvailable}
            />
          </div>
        </div>
        {isDirty && (
          <p className="helper-text mt-2 ml-auto">**Friendly reminder to save changes.</p>
        )}
        <button className="btn btn-secondary mt-4 ml-auto" type="submit" data-testid="submit">
          Save changes
        </button>
      </form>
    </FormProvider>
  );
};

export default Form;
